import { PermissionCode } from "../../permissions/permissions.js";
import {
  PM_CONSUMABLE_SLOTS,
  PM_CURRENT_TYPE_OPTIONS,
  PM_DIRECTION_OPTIONS,
  PM_FUNCTIONAL_CHECK_OPTIONS,
  PM_INTENSITY_UNIT_OPTIONS,
  PM_SURFACE_CONDITION_OPTIONS,
  emptyPmConsumableRef,
  type PmConsumableRef,
  type PmReport
} from "../../domain/ndt/pm-report-domain.js";
import { PM_TECHNIQUES, formatPmTechnique, type PmConsumable, type PmConsumableType } from "../../domain/ndt/pm-consumable.js";
import { illuminationOptionsFor } from "../../domain/ndt/lp-report-domain.js";
import { formatCivilDateBR, isLpConsumableExpired } from "../../domain/ndt/lp-consumable.js";
import { suggestNextItemNumber } from "../../domain/ndt/ndt-report-common.js";
import { calibrationStatusLabel, resolveCalibrationStatus } from "../../domain/ndt/ndt-calibration.js";
import { NdtReportPageBase, type NdtReportPageConfig } from "./ndt-report-page-base.js";

type SlotField = (typeof PM_CONSUMABLE_SLOTS)[number]["field"];

/**
 * RELATÓRIO DE ENSAIO POR PARTÍCULAS MAGNÉTICAS — página de elaboração.
 * Cards 01–08 na ordem do FORM-PM; Yoke e consumíveis vêm dos cadastros
 * reais de END (Cadastro de Equipamentos de END → Yokes / Consumíveis PM).
 */
class AppPmReportPage extends NdtReportPageBase<PmReport> {
  protected readonly config: NdtReportPageConfig = {
    method: "PM",
    documentType: "PM",
    title: "Relatório de Ensaio por Partículas Magnéticas (PM)",
    subtitle: "Elaboração e registro técnico do ensaio por Partículas Magnéticas.",
    routeBase: "/ndt/pm",
    reportLabel: "Relatório PM",
    serviceKey: "pmReportService",
    permissions: {
      view: PermissionCode.PmReportView, create: PermissionCode.PmReportCreate, edit: PermissionCode.PmReportEdit, validate: PermissionCode.PmReportValidate,
      generatePdf: PermissionCode.PmReportGeneratePdf, approve: PermissionCode.PmReportApprove, sign: PermissionCode.PmReportSign, manageSignatories: PermissionCode.PmReportManageSignatories
    },
    sections: [["01", "Identificação"], ["02", "Dados do Ensaio"], ["03", "Equipamento e Consumíveis"], ["04", "Registro da Inspeção"], ["05", "Croqui"], ["06", "Observações e Parecer"], ["07", "Evidências Fotográficas"], ["08", "Signatários"]],
    itemPrefix: "PM"
  };

  private yokes: any[] = [];
  private consumables: Record<PmConsumableType, PmConsumable[]> = { MAGNETIC_PARTICLES: [], CONTRAST_PAINT: [], CARRIER_SUSPENSION: [] };

  protected async loadSpecific(): Promise<void> {
    const r = this.report;
    const ctx = this.scopedCtx(r?.clientId);
    const svc = window.app.platform.pmConsumableService;
    const [particles, paint, carrier, yokes] = await Promise.all([
      svc.listSelectableForReport(ctx, "MAGNETIC_PARTICLES", r?.particles.consumableId ?? null).catch(() => []),
      svc.listSelectableForReport(ctx, "CONTRAST_PAINT", r?.contrastPaint.consumableId ?? null).catch(() => []),
      svc.listSelectableForReport(ctx, "CARRIER_SUSPENSION", r?.carrier.consumableId ?? null).catch(() => []),
      window.app.platform.ndtAssetService.listAssets(ctx, "MAGNETIC_YOKE", true).catch(() => [])
    ]);
    this.consumables = { MAGNETIC_PARTICLES: particles, CONTRAST_PAINT: paint, CARRIER_SUSPENSION: carrier };
    // Só o Yoke INATIVO some; um certificado vencido continua selecionável e o relatório avisa.
    this.yokes = (yokes || []).filter((a: any) => a.status !== "INACTIVE" || (!!r?.yokeAssetId && a.id === r.yokeAssetId));
  }

  protected handleSpecificInput(target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, ds: DOMStringMap): boolean {
    if (!this.report) return false;
    if (ds["itemKey"] && ds["itemField"]) {
      const row = this.report.results.find(r => r.key === ds["itemKey"]);
      if (row) {
        (row as any)[ds["itemField"]!] = target.value;
        if (ds["itemField"] === "laudo") {
          const cell = this.querySelector(`[data-laudo-badge="${row.key}"]`);
          if (cell) cell.className = `laudo-badge ${this.laudoClass(row.laudo)}`;
        }
      }
      return true;
    }
    return false;
  }

  private async onYokeChange(assetId: string) {
    if (!this.report) return;
    const yoke = this.yokes.find(a => a.id === assetId);
    this.report.yokeAssetId = assetId || null;
    if (!yoke) {
      this.report.yokeDescription = ""; this.report.yokeSerialNumber = ""; this.report.yokeCertificate = ""; this.report.yokeCertificateDate = ""; this.report.yokeCertificateValidity = "";
    } else {
      this.report.yokeDescription = [yoke.manufacturer, yoke.model, yoke.internalCode].filter(Boolean).join(" / ");
      this.report.yokeSerialNumber = yoke.serialNumber || "";
      // Congelado no relatório: o certificado como está agora no cadastro.
      const cert = await this.latestCertificateOf(yoke.id);
      this.report.yokeCertificate = cert?.certificateNumber || "";
      this.report.yokeCertificateDate = cert?.issueDate || "";
      this.report.yokeCertificateValidity = cert?.expirationDate || "";
    }
    this.scheduleAutosave(); this.render();
  }

  private onConsumableChange(field: SlotField, consumableId: string) {
    if (!this.report) return;
    const slot = PM_CONSUMABLE_SLOTS.find(s => s.field === field)!;
    const item = this.consumables[slot.type].find(c => c.id === consumableId);
    const ref: PmConsumableRef = item ? { consumableId: item.id, brand: item.brand, product: item.product, lot: item.lot, validity: item.validity, technique: item.technique } : emptyPmConsumableRef();
    this.report[field] = ref;
    this.scheduleAutosave(); this.render();
  }

  private addResultItem() {
    if (!this.report) return;
    this.report.results.push({ key: `PM-ITEM-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, item: suggestNextItemNumber(this.report.results.map(r => r.item)), element: "", location: "", extension: "", findings: "", laudo: "" });
    this.scheduleAutosave(); this.render();
  }

  private removeResultItem(key: string) {
    if (!this.report) return;
    this.report.results = this.report.results.filter(r => r.key !== key);
    this.scheduleAutosave(); this.render();
  }

  // -------------------------------------------------------------- CARDS

  protected renderCards(r: PmReport, ro: boolean, canSign: boolean): string {
    return [
      this.renderCardIdentification(r, ro),
      this.renderCardTestData(r, ro),
      this.renderCardEquipment(r, ro),
      this.renderCardResults(r, ro),
      this.renderCardCroqui(r, ro, "05"),
      this.renderCardObservations(r, ro, "06"),
      this.renderCardPhotos(r, ro, "07"),
      this.renderCardSignatories(r, ro, canSign, "08")
    ].join("");
  }

  private renderCardIdentification(r: PmReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-01">
        <div class="card-head"><span class="card-title"><span class="card-num">01.</span> Identificação</span></div>
        <div class="grid cols-4">
          <label class="field"><span>Cliente</span><input class="ctl" readonly value="${this.esc(r.clientDisplayName || "—")}"></label>
          <label class="field"><span>Projeto / Contrato</span><input class="ctl" readonly value="${this.esc(r.projectContract || "—")}"></label>
          <label class="field"><span>Unidade / Área</span><input class="ctl" readonly value="${this.esc(r.unitArea || "—")}"></label>
          <label class="field"><span>Equipamento / TAG</span><input class="ctl" readonly value="${this.esc(r.equipmentTag || "—")}"></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Tipo de Equipamento</span><input class="ctl" readonly value="${this.esc(r.equipmentTypeLabel || "—")}"></label>
          <label class="field"><span>Componente / Elemento</span><input class="ctl" data-field="component" value="${this.esc(r.component)}" placeholder="Junta soldada, bocal…" ${this.dis(ro)}></label>
          <label class="field"><span>Material Base</span><input class="ctl" data-field="baseMaterial" value="${this.esc(r.baseMaterial)}" placeholder="Aço carbono ASTM A516" ${this.dis(ro)}></label>
          <label class="field"><span>Temperatura do Ensaio (°C)</span><input class="ctl" data-field="testTemperature" value="${this.esc(r.testTemperature)}" placeholder="28" ${this.dis(ro)}></label>
        </div>
        <div style="margin-top:12px;">${this.renderProcedureFields(r, ro)}</div>
      </section>`;
  }

  private renderCardTestData(r: PmReport, ro: boolean): string {
    const techniques = PM_TECHNIQUES.filter(t => t.code !== "");
    return `
      <section class="nr-card" id="nr-sec-02">
        <div class="card-head"><span class="card-title"><span class="card-num">02.</span> Dados do Ensaio</span></div>
        <div class="grid cols-4">
          <label class="field"><span>Condição Superficial</span><select class="ctl" data-field="surfaceCondition" ${this.dis(ro)}>${PM_SURFACE_CONDITION_OPTIONS.map(o => this.opt(o.value, o.label, r.surfaceCondition === o.value)).join("")}</select></label>
          ${r.surfaceCondition === "OUTRA" ? `<label class="field"><span>Condição Superficial — outra</span><input class="ctl" data-field="surfaceConditionOther" value="${this.esc(r.surfaceConditionOther)}" ${this.dis(ro)}></label>` : ""}
          <label class="field"><span>Técnica / Aplicação *</span><select class="ctl" data-field="technique" ${this.dis(ro)}>${this.opt("", "Selecione", !r.technique)}${techniques.map(t => this.opt(t.code, t.label, r.technique === t.code)).join("")}</select></label>
          <label class="field"><span>Método de Magnetização</span><input class="ctl" data-field="magnetizationMethod" value="${this.esc(r.magnetizationMethod)}" placeholder="Yoke" ${this.dis(ro)}></label>
          <label class="field"><span>Tipo de Corrente *</span><select class="ctl" data-field="currentType" ${this.dis(ro)}>${PM_CURRENT_TYPE_OPTIONS.map(o => this.opt(o.value, o.label, r.currentType === o.value)).join("")}</select></label>
          ${r.currentType === "OUTRO" ? `<label class="field"><span>Tipo de Corrente — outro</span><input class="ctl" data-field="currentTypeOther" value="${this.esc(r.currentTypeOther)}" ${this.dis(ro)}></label>` : ""}
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Direção / Sentido *</span><select class="ctl" data-field="magnetizationDirection" ${this.dis(ro)}>${PM_DIRECTION_OPTIONS.map(o => this.opt(o.value, o.label, r.magnetizationDirection === o.value)).join("")}</select></label>
          <label class="field"><span>Distância entre Polos (mm)</span><input class="ctl" data-field="poleSpacing" value="${this.esc(r.poleSpacing)}" placeholder="150" ${this.dis(ro)}></label>
          <label class="field"><span>Tipo de Iluminação *</span><select class="ctl" data-field="illuminationType" ${this.dis(ro)}>${illuminationOptionsFor(r.illuminationType).map(o => this.opt(o.value, o.label, r.illuminationType === o.value)).join("")}</select></label>
          <label class="field"><span>Intensidade Medida</span>
            <div style="display:flex; gap:6px;"><input class="ctl" data-field="illuminationIntensity" value="${this.esc(r.illuminationIntensity)}" placeholder="1.000" ${this.dis(ro)}><select class="ctl" data-field="illuminationUnit" style="width:110px;" ${this.dis(ro)}>${PM_INTENSITY_UNIT_OPTIONS.map(o => this.opt(o.value, o.label, r.illuminationUnit === o.value)).join("")}</select></div></label>
        </div>
        <div class="grid cols-2" style="margin-top:12px;">
          <label class="field"><span>Observação Técnica</span><input class="ctl" data-field="technicalNote" value="${this.esc(r.technicalNote)}" ${this.dis(ro)}></label>
        </div>
      </section>`;
  }

  private renderConsumableSelect(r: PmReport, field: SlotField, ro: boolean): string {
    const slot = PM_CONSUMABLE_SLOTS.find(s => s.field === field)!;
    const ref = r[field];
    const options = this.consumables[slot.type];
    const expired = isLpConsumableExpired(ref.validity);
    return `
      <div class="field">
        <span>${slot.label}${slot.required ? " *" : ""}</span>
        <select class="ctl" data-consumable="${field}" ${this.dis(ro)}>${this.opt("", options.length === 0 ? `Nenhum consumível "${slot.label}" cadastrado` : "Selecione...", !ref.consumableId)}${options.map(c => this.opt(c.id, `${c.brand} — ${c.product} · Lote ${c.lot}`, ref.consumableId === c.id)).join("")}</select>
        ${ref.consumableId || ref.product ? `
          <div class="consumable-strip ${expired ? "expired" : ""}">
            <span><b>Marca:</b> ${this.esc(ref.brand || "—")}</span><span><b>Produto:</b> ${this.esc(ref.product || "—")}</span>
            <span><b>Lote:</b> ${this.esc(ref.lot || "—")}</span><span class="validity"><b>Validade:</b> ${ref.validity ? this.esc(formatCivilDateBR(ref.validity)) : "—"}${expired ? " ⚠ VENCIDO" : ""}</span>
            <span><b>Técnica:</b> ${this.esc(formatPmTechnique(ref.technique) || "—")}</span>
          </div>` : `<small class="hint">Cadastre em END → Cadastro de Equipamentos de END → Consumíveis PM.</small>`}
      </div>`;
  }

  private renderCardEquipment(r: PmReport, ro: boolean): string {
    const status = r.yokeAssetId ? resolveCalibrationStatus(r.yokeCertificateValidity) : null;
    return `
      <section class="nr-card" id="nr-sec-03">
        <div class="card-head"><span class="card-title"><span class="card-num">03.</span> Equipamento e Consumíveis</span></div>
        <div class="grid cols-4">
          <label class="field" style="grid-column: span 2;"><span>Yoke / Equipamento PM *</span>
            <select class="ctl" id="sel-yoke" ${this.dis(ro)}>${this.opt("", this.yokes.length === 0 ? "Nenhum Yoke cadastrado para este cliente" : "Selecione...", !r.yokeAssetId)}${this.yokes.map((y: any) => this.opt(y.id, `${[y.internalCode, y.manufacturer, y.model].filter(Boolean).join(" · ")}${y.serialNumber ? " — S/N " + y.serialNumber : ""}`, r.yokeAssetId === y.id)).join("")}</select>
            <small class="hint">Cadastre em END → Cadastro de Equipamentos de END → Yokes.</small></label>
          <label class="field"><span>Verificação Funcional</span><select class="ctl" data-field="functionalCheck" ${this.dis(ro)}>${PM_FUNCTIONAL_CHECK_OPTIONS.map(o => this.opt(o.value, o.label, r.functionalCheck === o.value)).join("")}</select></label>
          <label class="field"><span>Registro da Verificação</span><input class="ctl" data-field="functionalCheckNote" value="${this.esc(r.functionalCheckNote)}" placeholder="Registro aplicável" ${this.dis(ro)}></label>
        </div>
        ${r.yokeAssetId ? `
          <div class="asset-strip">
            <span><b>Marca / Modelo / Identificação</b>${this.esc(r.yokeDescription || "—")}</span>
            <span><b>Nº de Série</b>${this.esc(r.yokeSerialNumber || "—")}</span>
            <span><b>Certificado</b>${this.esc(r.yokeCertificate || "NÃO INFORMADO")}</span>
            <span><b>Data da Calibração</b>${r.yokeCertificateDate ? this.esc(formatCivilDateBR(r.yokeCertificateDate.slice(0, 10))) : "—"}</span>
            <span><b>Validade</b>${r.yokeCertificateValidity ? this.esc(formatCivilDateBR(r.yokeCertificateValidity.slice(0, 10))) : "—"}</span>
            <span><b>Calibração</b><span class="${status === "EXPIRED" ? "warn" : ""}">${this.esc(calibrationStatusLabel(r.yokeCertificateValidity))}</span></span>
          </div>` : ""}
        <div class="grid cols-3" style="margin-top:14px; align-items:start;">
          ${this.renderConsumableSelect(r, "particles", ro)}
          ${this.renderConsumableSelect(r, "contrastPaint", ro)}
          ${this.renderConsumableSelect(r, "carrier", ro)}
        </div>
      </section>`;
  }

  private renderCardResults(r: PmReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-04">
        <div class="card-head"><span class="card-title"><span class="card-num">04.</span> Registro da Inspeção / Resultados</span>${!ro ? `<button class="btn sm primary" id="btn-add-item"><i data-lucide="plus"></i> Adicionar Item</button>` : ""}</div>
        <div class="table-wrap"><table class="nr-table">
          <thead><tr><th style="width:56px;">Nº</th><th style="width:150px;">Junta / Elemento</th><th style="width:140px;">Localização</th><th style="width:100px;">Extensão (mm)</th><th>Indicações / Achados</th><th style="width:190px;">Laudo</th><th style="width:56px;">Ações</th></tr></thead>
          <tbody>${r.results.length === 0 ? `<tr><td colspan="7" class="empty">Nenhum item registrado. Use "Adicionar Item".</td></tr>` : r.results.map(row => `
            <tr>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="item" value="${this.esc(row.item)}" ${this.dis(ro)}></td>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="element" value="${this.esc(row.element)}" placeholder="J-01" ${this.dis(ro)}></td>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="location" value="${this.esc(row.location)}" placeholder="0° (Topo)" ${this.dis(ro)}></td>
              <td><input class="cell num" data-item-key="${row.key}" data-item-field="extension" value="${this.esc(row.extension)}" placeholder="12" ${this.dis(ro)}></td>
              <td><input class="cell" data-item-key="${row.key}" data-item-field="findings" value="${this.esc(row.findings)}" placeholder="Indicação linear relevante" ${this.dis(ro)}></td>
              <td>${this.renderLaudoSelect(row.key, "item-key", "item-field", row.laudo, ro)}</td>
              <td style="text-align:center;">${!ro ? `<button class="icon-btn danger" data-remove-item="${row.key}" title="Remover"><i data-lucide="trash-2"></i></button>` : ""}</td>
            </tr>`).join("")}</tbody>
        </table></div>
      </section>`;
  }

  protected attachSpecificEvents(ro: boolean): void {
    if (ro) return;
    this.querySelector("#sel-yoke")?.addEventListener("change", e => this.onYokeChange((e.target as HTMLSelectElement).value));
    this.querySelectorAll("[data-consumable]").forEach(sel => sel.addEventListener("change", e => this.onConsumableChange((sel as HTMLElement).dataset["consumable"] as SlotField, (e.target as HTMLSelectElement).value)));
    this.querySelector("#btn-add-item")?.addEventListener("click", () => this.addResultItem());
    this.querySelectorAll("[data-remove-item]").forEach(btn => btn.addEventListener("click", () => this.removeResultItem((btn as HTMLElement).dataset["removeItem"]!)));
    // Re-render nos selects que abrem campo "outro" ou trocam o rótulo da unidade.
    this.querySelectorAll("[data-field='surfaceCondition'],[data-field='currentType']").forEach(sel => sel.addEventListener("change", () => this.render()));
  }
}

if (!customElements.get("app-pm-report-page")) {
  customElements.define("app-pm-report-page", AppPmReportPage);
}
